import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/ui/dialog';
import { Button } from '@shadcn/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shadcn/components/ui/card';
import { 
  User, 
  Calendar, 
  Package, 
  Store, 
  ExternalLink,
  Paperclip,
  Eye,
  Download,
  X,
  ShoppingCart,
  Truck,
  Clock,
  Wrench
} from 'lucide-react';

import { StatusBadge } from '../TicketSystem/StatusBadge';
import { TicketTypeIcon } from '../TicketSystem/TicketTypeIcon';
import { TICKET_TYPE_LABELS } from '../../constants';

/**
 * Format date for display
 */
const formatDate = (dateString) => {
  if (!dateString) return '—';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '—';
  }
};

/**
 * Image attachment gallery component
 */
const AttachmentGallery = ({ attachments }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  if (!attachments || attachments.length === 0) return null;

  return (
    <div className="space-y-3">
      <h4 className="font-medium text-gray-900 flex items-center gap-2">
        <Paperclip className="w-4 h-4" />
        Attachments ({attachments.length})
      </h4>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {attachments.map((url, index) => (
          <div key={index} className="relative group">
            <img
              src={url}
              alt={`Attachment ${index + 1}`}
              className="w-full h-24 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => setSelectedImage(url)}
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity rounded-lg flex items-center justify-center">
              <Eye className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
        ))}
      </div>

      {/* Image preview modal */}
      {selectedImage && (
        <Dialog open={true} onOpenChange={() => setSelectedImage(null)}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Attachment Preview</DialogTitle>
              <Button
                variant="outline"
                size="sm"
                className="absolute top-4 right-4"
                onClick={() => setSelectedImage(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </DialogHeader>
            <div className="flex justify-center">
              <img
                src={selectedImage}
                alt="Attachment preview"
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                onClick={() => window.open(selectedImage, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Original
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = selectedImage;
                  link.download = `attachment-${Date.now()}`;
                  link.click();
                }}
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

/**
 * Replacement SKUs table component
 */
const ReplacementSkusTable = ({ replacementSkus }) => {
  if (!replacementSkus || replacementSkus.length === 0) return null;

  return (
    <div className="space-y-3">
      <h4 className="font-medium text-gray-900 flex items-center gap-2">
        <Package className="w-4 h-4" />
        Replacement SKUs ({replacementSkus.length})
      </h4>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse border border-gray-200 rounded-lg">
          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-200 px-3 py-2 text-left font-medium">Original SKU</th>
              <th className="border border-gray-200 px-3 py-2 text-left font-medium">Replacement SKU</th>
              <th className="border border-gray-200 px-3 py-2 text-left font-medium">Monogram</th>
              <th className="border border-gray-200 px-3 py-2 text-left font-medium">Shipping</th>
              <th className="border border-gray-200 px-3 py-2 text-left font-medium">Date Needed</th>
            </tr>
          </thead>
          <tbody>
            {replacementSkus.map((sku, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="border border-gray-200 px-3 py-2 font-mono text-xs">
                  {sku.originalSku}
                </td>
                <td className="border border-gray-200 px-3 py-2 font-mono text-xs">
                  {sku.replacementSku}
                </td>
                <td className="border border-gray-200 px-3 py-2">
                  {sku.monogram && sku.monogram[0] ? (
                    <div className="text-xs">
                      <div><strong>{sku.monogram[0].text}</strong></div>
                      <div className="text-gray-500">
                        {sku.monogram[0].font} • {sku.monogram[0].color} • {sku.monogram[0].location}
                      </div>
                    </div>
                  ) : (
                    <span className="text-gray-400">No monogram</span>
                  )}
                </td>
                <td className="border border-gray-200 px-3 py-2">
                  <div className="flex items-center gap-1">
                    {sku.toBeShipped ? (
                      <>
                        <Truck className="w-3 h-3 text-blue-600" />
                        <span className="text-blue-600 text-xs">Ship</span>
                      </>
                    ) : (
                      <>
                        <Store className="w-3 h-3 text-green-600" />
                        <span className="text-green-600 text-xs">Pickup</span>
                      </>
                    )}
                  </div>
                </td>
                <td className="border border-gray-200 px-3 py-2 text-xs">
                  {sku.dateNeededBy ? formatDate(sku.dateNeededBy) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Repair SKUs table component
 */
const RepairSkuTable = ({ repairSkus }) => {
  if (!repairSkus || repairSkus.length === 0) return null;

  return (
    <div className="space-y-3">
      <h4 className="font-medium text-gray-900 flex items-center gap-2">
        <Wrench className="w-4 h-4" />
        Repair Items ({repairSkus.length})
      </h4>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse border border-gray-200 rounded-lg">
          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-200 px-3 py-2 text-left font-medium">SKU</th>
              <th className="border border-gray-200 px-3 py-2 text-left font-medium">Repair Types</th>
              <th className="border border-gray-200 px-3 py-2 text-left font-medium">Parts</th>
              <th className="border border-gray-200 px-3 py-2 text-left font-medium">Shipping</th>
              <th className="border border-gray-200 px-3 py-2 text-left font-medium">Date Needed</th>
            </tr>
          </thead>
          <tbody>
            {repairSkus.map((item, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="border border-gray-200 px-3 py-2 font-mono text-xs">
                  {item.sku}
                </td>
                <td className="border border-gray-200 px-3 py-2">
                  {item.repairTypes && item.repairTypes.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {item.repairTypes.map((type, typeIndex) => (
                        <span
                          key={typeIndex}
                          className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="border border-gray-200 px-3 py-2">
                  {item.parts && item.parts.length > 0 ? (
                    <div className="space-y-1">
                      {item.parts.map((part, partIndex) => (
                        <div key={partIndex} className="text-xs">
                          <div className="font-medium">{part.name}</div>
                          <div className="text-gray-500">Qty: {part.quantity}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-400">No parts</span>
                  )}
                </td>
                <td className="border border-gray-200 px-3 py-2">
                  <div className="flex items-center gap-1">
                    {item.toBeShipped ? (
                      <>
                        <Truck className="w-3 h-3 text-blue-600" />
                        <span className="text-blue-600 text-xs">Ship</span>
                      </>
                    ) : (
                      <>
                        <Store className="w-3 h-3 text-green-600" />
                        <span className="text-green-600 text-xs">Pickup</span>
                      </>
                    )}
                  </div>
                </td>
                <td className="border border-gray-200 px-3 py-2 text-xs">
                  {item.dateNeededBy ? formatDate(item.dateNeededBy) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Info row component
 */
const InfoRow = ({ label, value, icon, className = "" }) => (
  <div className={`flex items-start justify-between py-2 ${className}`}>
    <span className="text-sm text-gray-600 flex items-center gap-2 min-w-0 flex-1">
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {label}:
    </span>
    <span className="text-sm font-medium text-gray-900 text-right ml-4">
      {value || '—'}
    </span>
  </div>
);

/**
 * Enhanced TicketDetailModal Component
 */
const TicketDetailModal = ({ ticket, onClose }) => {
  if (!ticket) return null;

  const { details } = ticket;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-white z-[1600]">
        
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center gap-3">
            <TicketTypeIcon type={ticket.ticketType} size="lg" />
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xl font-bold text-gray-900">
                  {TICKET_TYPE_LABELS[ticket.ticketType]} Ticket
                </span>
                <StatusBadge 
                  metaStatus={ticket.status?.metaStatus}
                  originalStatus={ticket.status?.originalStatus}
                />
              </div>
              <div className="text-sm text-gray-600 font-normal mt-1">
                Ticket ID: <span className="font-mono">{ticket.id}</span>
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-4">
          {/* Left Column - Main Information */}
          <div className="space-y-6">
            {/* Basic Information Card */}
            <Card className="border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-0">
                <InfoRow
                  label="Customer Name"
                  value={ticket.customerName}
                  icon={<User className="w-4 h-4" />}
                />
                <InfoRow
                  label="Staff Name"
                  value={ticket.staffName}
                  icon={<User className="w-4 h-4" />}
                />
                <InfoRow
                  label="Store Name"
                  value={ticket.storeName}
                  icon={<Store className="w-4 h-4" />}
                />
                <InfoRow
                  label="Created Date"
                  value={formatDate(ticket.createdAt)}
                  icon={<Calendar className="w-4 h-4" />}
                />
                {ticket.orderNumber && (
                  <InfoRow
                    label="Order Number"
                    value={ticket.orderNumber}
                    icon={<ShoppingCart className="w-4 h-4" />}
                  />
                )}
              </CardContent>
            </Card>

            {/* Status Information Card */}
            <Card className="border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Status Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-0">
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-600">Meta Status:</span>
                  <StatusBadge 
                    metaStatus={ticket.status?.metaStatus}
                    showOriginal={false}
                  />
                </div>
                <InfoRow
                  label="Original Status"
                  value={ticket.status?.originalStatus}
                />
              </CardContent>
            </Card>

            {/* Warranty/Order specific details */}
            {details && (
              <Card className="border-gray-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Ticket Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-0">
                  {details.faultReason && (
                    <InfoRow label="Fault Reason" value={details.faultReason} />
                  )}
                  {details.isFulfilled !== undefined && (
                    <InfoRow 
                      label="Fulfilled" 
                      value={details.isFulfilled ? "Yes" : "No"} 
                    />
                  )}
                  {details.dayNeededBy && (
                    <InfoRow
                      label="Day Needed By"
                      value={formatDate(details.dayNeededBy)}
                      icon={<Clock className="w-4 h-4" />}
                    />
                  )}
                  {details.toBeShipped !== undefined && (
                    <InfoRow
                      label="To Be Shipped"
                      value={details.toBeShipped ? "Yes" : "No"}
                      icon={details.toBeShipped ? <Truck className="w-4 h-4" /> : <Store className="w-4 h-4" />}
                    />
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Detailed Information */}
          <div className="space-y-6">
            {/* Action Description / Customer Request */}
            {(details?.actionDescription || details?.customerRequest) && (
              <Card className="border-gray-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">
                    {details.actionDescription ? 'Action Description' : 'Customer Request'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {details.actionDescription || details.customerRequest}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Attachments */}
            {details?.attachments && (
              <Card className="border-gray-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Attachments</CardTitle>
                </CardHeader>
                <CardContent>
                  <AttachmentGallery attachments={details.attachments} />
                </CardContent>
              </Card>
            )}

            {/* Replacement SKUs */}
            {details?.replacementSkus && (
              <Card className="border-gray-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Replacement Items</CardTitle>
                </CardHeader>
                <CardContent>
                  <ReplacementSkusTable replacementSkus={details.replacementSkus} />
                </CardContent>
              </Card>
            )}
            {console.log("details", details)}

            {/* Repairs */}
            {details?.itemRepairs && (
              <Card className="border-gray-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Repairs</CardTitle>
                </CardHeader>
                <CardContent>
                  <RepairSkuTable repairSkus={details.itemRepairs} />
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t pt-4 mt-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Last updated: {formatDate(ticket.updatedAt || ticket.createdAt)}
            </div>
            
            <div className="flex items-center gap-3">
              {ticket.externalUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(ticket.externalUrl, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Original
                </Button>
              )}
              
              <Button onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TicketDetailModal;
